import { Box, Button, Skeleton, Typography } from "@mui/material";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import SectionCard from "./SectionCard";
import RevisionFilters from "./RevisionFilters";
import RevisionItem from "./RevisionItem";
import { RevisionFilterState, TemplateRevision } from "../types";
import { tokens } from "../tokens";

interface Props {
  revisions: TemplateRevision[];
  /** True while the list is being re-fetched for a newly selected range. */
  loading?: boolean;
  filters: RevisionFilterState;
  editors: string[];
  onFiltersChange: (filters: RevisionFilterState) => void;
  onViewRevision: (revision: TemplateRevision) => void;
}

const SHORT_RANGES = ["today", "yesterday", "7d"];

const RevisionHistoryCard = ({
  revisions,
  loading = false,
  filters,
  editors,
  onFiltersChange,
  onViewRevision,
}: Props) => {
  const showLoadMore = SHORT_RANGES.includes(filters.range);

  return (
    <SectionCard
      title="Revision History"
      subtitle="Every save of this template, newest first"
      disablePadding
      collapsible
      defaultExpanded={false}
    >
      <RevisionFilters
        filters={filters}
        editors={editors}
        onChange={onFiltersChange}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "9px 22px",
          fontSize: 11.5,
          fontWeight: 500,
          color: tokens.textFaint,
          backgroundColor: tokens.bg,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Typography sx={{ fontSize: 11.5 }}>
          {loading
            ? "Loading revisions…"
            : `${revisions.length} revision${
                revisions.length === 1 ? "" : "s"
              }`}
        </Typography>
        <Typography sx={{ fontSize: 11.5 }}>
          Sorted by most recently modified
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 380, overflowY: "auto", padding: "6px 12px" }}>
        {loading ? (
          <Box sx={{ padding: "8px 10px" }}>
            {[0, 1, 2].map((row) => (
              <Box key={row} sx={{ display: "flex", gap: 1.5, py: 1.4 }}>
                <Skeleton
                  variant="circular"
                  width={9}
                  height={9}
                  sx={{ mt: 0.6 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="28%" sx={{ fontSize: 13 }} />
                  <Skeleton variant="text" width="52%" sx={{ fontSize: 12 }} />
                  <Skeleton variant="text" width="44%" sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : revisions.length ? (
          revisions.map((revision, index) => (
            <RevisionItem
              key={revision.revisionId}
              revision={revision}
              isLast={index === revisions.length - 1}
              onView={onViewRevision}
            />
          ))
        ) : (
          <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
            <HistoryToggleOffIcon
              sx={{ fontSize: 36, color: tokens.borderStrong, mb: 1.5 }}
            />
            <Typography sx={{ fontSize: 13, color: tokens.textFaint }}>
              No revisions in this range
              {filters.modifiedBy ? ` modified by "${filters.modifiedBy}"` : ""}
              .
            </Typography>
          </Box>
        )}
      </Box>

      {showLoadMore && (
        <Box
          sx={{
            padding: "14px 20px",
            textAlign: "center",
            borderTop: `1px solid ${tokens.border}`,
          }}
        >
          <Button
            onClick={() => onFiltersChange({ ...filters, range: "30d" })}
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: tokens.accent,
              textTransform: "none",
            }}
          >
            Load 30 more days of history
          </Button>
          <Typography
            sx={{ fontSize: 11, color: tokens.textFaint, display: "block" }}
          >
            Showing revisions from the selected range
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};

export default RevisionHistoryCard;
